import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  Input,
  InputNumber,
  Radio,
  Button,
  Alert,
  Descriptions,
  Tag,
  Space,
  Spin,
  List,
  Typography,
  Select,
  message,
  Result,
} from 'antd';
import {
  ThunderboltOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  InfoCircleOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { proposalApi } from '../../services/proposal';
import { POLL_CONFIG, showPollTimeoutNotification } from '../../utils/polling';
import type { RequirementAnalysis } from '../../types/proposal';
import { COLORS } from '../../constants/colors';

const { TextArea } = Input;
const { Text } = Typography;

// 来源类型
type SourceType = 'email' | 'wechat' | 'whatsapp' | 'other';

// 来源选项
const sourceOptions = [
  { value: 'email', label: '邮件' },
  { value: 'wechat', label: '微信' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'other', label: '其他' },
];

// 字段标签映射
const fieldLabelMap: Record<string, string> = {
  company_name: '公司名称',
  brand: '品牌',
  industry: '所属行业',
  website: '公司网站',
  product_name: '产品名称',
  product_category: '产品类别',
  product_links: '产品链接',
  competitors: '竞品信息',
  target_countries: '目标国家',
  target_platform: '目标平台',
  follower_tiers: '粉丝分层',
  content_types: '内容类型',
  budget_range: '预算范围',
  timeline: '时间线',
  influencer_count: '红人数量',
};

// 来源标签组件
const SourceTag: React.FC<{ source: string }> = ({ source }) => {
  const sourceMap: Record<string, { color: string; text: string }> = {
    extracted: { color: 'blue', text: '原文提取' },
    inferred: { color: 'purple', text: 'AI推断' },
    kb_suggested: { color: 'green', text: '知识库建议' },
  };
  const config = sourceMap[source] || { color: 'default', text: source };
  return <Tag color={config.color} style={{ marginLeft: 8 }}>{config.text}</Tag>;
};

// 状态标签组件
const StatusAlert: React.FC<{
  status: string;
  confidenceScore?: number;
}> = ({ status, confidenceScore }) => {
  if (status === 'draft') {
    return (
      <Alert
        type="info"
        message={
          <Space>
            <span>状态：待确认</span>
            {confidenceScore !== undefined && (
              <Tag color="blue">置信度：{Math.round(confidenceScore * 100)}%</Tag>
            )}
          </Space>
        }
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }
  if (status === 'confirmed') {
    return (
      <Alert
        type="success"
        message={
          <Space>
            <span>状态：已确认</span>
            {confidenceScore !== undefined && (
              <Tag color="green">置信度：{Math.round(confidenceScore * 100)}%</Tag>
            )}
          </Space>
        }
        icon={<CheckCircleOutlined />}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }
  return null;
};

// 简单文本字段列表
const TEXT_FIELDS = ['company_name', 'brand', 'industry', 'product_name', 'product_category', 'timeline', 'website'];
const TEXTAREA_FIELDS = ['next_steps'];
const ARRAY_FIELDS = ['product_links', 'competitors', 'competitor_links', 'content_types'];
const NUMBER_FIELDS = ['influencer_count'];

// 货币选项
const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'CNY', label: 'CNY' },
  { value: 'JPY', label: 'JPY' },
];

// 渲染字段值（只读模式）
const renderFieldValueReadonly = (value: unknown, fieldKey: string): React.ReactNode => {
  if (value === null || value === undefined || value === '') {
    return <Text type="danger">待补充</Text>;
  }

  // 粉丝分层：数组对象 [{tier, range, count}]
  if (fieldKey === 'follower_tiers' && Array.isArray(value)) {
    if (value.length === 0) return <Text type="danger">待补充</Text>;
    const tierColorMap: Record<string, string> = {
      mega: 'red',
      macro: 'orange',
      mid: 'blue',
      micro: 'green',
      nano: 'cyan',
    };
    return (
      <Space wrap>
        {value.map((item, index) => {
          if (typeof item === 'object' && item !== null) {
            const obj = item as Record<string, unknown>;
            const tier = String(obj.tier || obj.name || obj.range || '未知');
            const count = obj.count;
            const range = obj.range;
            const color = tierColorMap[tier.toLowerCase()] || 'default';
            const label = count
              ? `${tier} (${count}个)`
              : range && range !== tier
                ? `${tier} (${range})`
                : tier;
            return <Tag key={index} color={color}>{label}</Tag>;
          }
          return <Tag key={index}>{String(item)}</Tag>;
        })}
      </Space>
    );
  }

  // 预算范围：{min, max, currency}
  if (fieldKey === 'budget_range' && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    const min = obj.min;
    const max = obj.max;
    const currency = (obj.currency as string) || 'USD';
    if (min !== undefined && max !== undefined) {
      const formatNum = (n: unknown) => Number(n).toLocaleString('en-US');
      return <Text strong>{currency} {formatNum(min)} - {formatNum(max)}</Text>;
    }
    // 没有 min/max 则降级为通用对象渲染
  }

  // 通用数组
  if (Array.isArray(value)) {
    if (value.length === 0) return <Text type="danger">待补充</Text>;
    return (
      <Space wrap>
        {value.map((item, index) => (
          <Tag key={index}>{typeof item === 'object' ? JSON.stringify(item) : String(item)}</Tag>
        ))}
      </Space>
    );
  }

  // 通用对象：渲染为 key-value Tag 列表
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const entries = Object.entries(obj);
    if (entries.length === 0) return <Text type="danger">待补充</Text>;
    return (
      <Space wrap>
        {entries.map(([key, val]) => (
          <Tag key={key}>{fieldLabelMap[key] || key}: {String(val)}</Tag>
        ))}
      </Space>
    );
  }

  return <Text>{String(value)}</Text>;
};

interface RequirementTabProps {
  proposalId: string;
  requirement?: RequirementAnalysis | null;
  onRefresh: () => void;
}

export const RequirementTab: React.FC<RequirementTabProps> = ({
  proposalId,
  requirement,
  onRefresh,
}) => {
  const [inputContent, setInputContent] = useState('');
  const [source, setSource] = useState<SourceType>('email');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedFields, setEditedFields] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 获取字段显示值：编辑中 > 用户修改 > AI 解析
  const getFieldValue = useCallback((fieldKey: string): unknown => {
    const userModified = requirement?.user_modified_result || {};
    const parsed = requirement?.parsed_result || {};
    if (isEditing && fieldKey in editedFields) {
      return editedFields[fieldKey];
    }
    if (fieldKey in userModified && userModified[fieldKey] !== null && userModified[fieldKey] !== undefined) {
      return userModified[fieldKey];
    }
    return parsed[fieldKey];
  }, [requirement, isEditing, editedFields]);

  // 检查字段是否已被用户修改
  const isUserModified = useCallback((fieldKey: string): boolean => {
    const userModified = requirement?.user_modified_result || {};
    return fieldKey in userModified && userModified[fieldKey] !== null && userModified[fieldKey] !== undefined;
  }, [requirement]);

  // 更新编辑中的字段值
  const handleFieldChange = useCallback((fieldKey: string, value: unknown) => {
    setEditedFields(prev => ({ ...prev, [fieldKey]: value }));
  }, []);

  // 保存编辑
  const handleSave = useCallback(async () => {
    if (!requirement?.id || Object.keys(editedFields).length === 0) {
      message.info('没有需要保存的修改');
      setIsEditing(false);
      return;
    }
    try {
      setSaving(true);
      await proposalApi.updateRequirement(proposalId, requirement.id, {
        user_modified: editedFields,
      });
      message.success('保存成功');
      setIsEditing(false);
      setEditedFields({});
      onRefresh();
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  }, [requirement, editedFields, proposalId, onRefresh]);

  // 取消编辑
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedFields({});
  }, []);

  // 渲染编辑控件
  const renderEditableField = useCallback((fieldKey: string, value: unknown): React.ReactNode => {
    // budget_range 特殊处理
    if (fieldKey === 'budget_range') {
      const budgetObj = (typeof value === 'object' && value !== null && !Array.isArray(value))
        ? value as Record<string, unknown>
        : { min: undefined, max: undefined, currency: 'USD' };
      return (
        <Space>
          <InputNumber
            placeholder="最小"
            value={budgetObj.min as number | undefined}
            onChange={(v) => handleFieldChange(fieldKey, { ...budgetObj, min: v })}
            style={{ width: 120 }}
          />
          <span>-</span>
          <InputNumber
            placeholder="最大"
            value={budgetObj.max as number | undefined}
            onChange={(v) => handleFieldChange(fieldKey, { ...budgetObj, max: v })}
            style={{ width: 120 }}
          />
          <Select
            value={(budgetObj.currency as string) || 'USD'}
            onChange={(v) => handleFieldChange(fieldKey, { ...budgetObj, currency: v })}
            options={CURRENCY_OPTIONS}
            style={{ width: 80 }}
          />
        </Space>
      );
    }

    // target_countries: 对象 { "country": percentage }
    if (fieldKey === 'target_countries') {
      const countries = (typeof value === 'object' && value !== null && !Array.isArray(value))
        ? Object.keys(value as Record<string, unknown>)
        : Array.isArray(value) ? (value as unknown[]).map(String) : [];
      return (
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="输入国家名称后按回车"
          value={countries}
          onChange={(vals: string[]) => {
            const obj: Record<string, number> = {};
            vals.forEach(v => { obj[v] = 0; });
            handleFieldChange(fieldKey, obj);
          }}
        />
      );
    }

    // follower_tiers: 简化为 tags 输入
    if (fieldKey === 'follower_tiers') {
      const tiers = Array.isArray(value)
        ? (value as Array<Record<string, unknown>>).map(item =>
            typeof item === 'object' && item !== null ? String((item as Record<string, unknown>).tier || item) : String(item)
          )
        : [];
      return (
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="输入粉丝分层（如 mega, macro, mid）后按回车"
          value={tiers}
          onChange={(vals: string[]) => {
            handleFieldChange(fieldKey, vals.map(v => ({ tier: v })));
          }}
        />
      );
    }

    // 数组字段
    if (ARRAY_FIELDS.includes(fieldKey)) {
      const arrVal = Array.isArray(value)
        ? (value as unknown[]).map(item => typeof item === 'object' ? JSON.stringify(item) : String(item))
        : [];
      return (
        <Select
          mode="tags"
          style={{ width: '100%' }}
          placeholder="输入后按回车添加"
          value={arrVal}
          onChange={(vals: string[]) => handleFieldChange(fieldKey, vals)}
        />
      );
    }

    // 数字字段
    if (NUMBER_FIELDS.includes(fieldKey)) {
      return (
        <InputNumber
          value={value as number | undefined}
          onChange={(v) => handleFieldChange(fieldKey, v)}
          style={{ width: 200 }}
          min={0}
          placeholder="请输入数量"
        />
      );
    }

    // TextArea 字段
    if (TEXTAREA_FIELDS.includes(fieldKey)) {
      return (
        <Input.TextArea
          value={value != null ? String(value) : ''}
          onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
          rows={3}
          placeholder={`请输入${fieldLabelMap[fieldKey] || fieldKey}`}
        />
      );
    }

    // 默认: 简单文本输入
    return (
      <Input
        value={value != null ? String(value) : ''}
        onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
        placeholder={`请输入${fieldLabelMap[fieldKey] || fieldKey}`}
      />
    );
  }, [handleFieldChange]);

  // 渲染字段值（支持编辑模式 + 用户修改标记）
  const renderFieldValue = useCallback((fieldKey: string): React.ReactNode => {
    const value = getFieldValue(fieldKey);
    if (isEditing) {
      return renderEditableField(fieldKey, value);
    }
    const node = renderFieldValueReadonly(value, fieldKey);
    if (isUserModified(fieldKey)) {
      return (
        <Space>
          <span style={{ color: COLORS.primary }}>{node}</span>
          <EditOutlined style={{ color: COLORS.primary, fontSize: 12 }} />
        </Space>
      );
    }
    return node;
  }, [getFieldValue, isEditing, renderEditableField, isUserModified]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // 开始AI解析
  const handleAnalyze = async (content?: unknown) => {
    let text: string;

    if (typeof content === 'string') {
      text = content.trim();
    } else if (content === undefined || content === null) {
      text = (inputContent || '').trim();
    } else {
      // Object 类型不应该直接转为字符串，回退到 inputContent
      console.warn('[RequirementTab] Unexpected content type:', typeof content, content);
      text = (inputContent || '').trim();
    }

    if (!text) {
      message.warning('请输入询盘内容');
      return;
    }

    setLoading(true);
    try {
      await proposalApi.analyzeRequirements(proposalId, {
        raw_content: text,
      });
      message.success('AI需求解析已启动');

      // 开始轮询
      startPolling();
    } catch (error) {
      message.error('启动需求解析失败，请重试');
      setLoading(false);
    }
  };

  // 轮询检查状态
  const startPolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    let pollAttempts = 0;
    timerRef.current = setInterval(async () => {
      pollAttempts++;
      if (pollAttempts >= POLL_CONFIG.MAX_ATTEMPTS) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
        showPollTimeoutNotification('poll-timeout-req-tab', () => {
          startPolling();
        });
        setLoading(false);
        return;
      }
      try {
        const response = await proposalApi.getRequirementAnalysis(proposalId);
        const result = response.data;
        console.log('[RequirementTab] polling result status:', result?.status, 'company_name:', result?.parsed_result?.company_name);
        if (result && (result.status === 'draft' || result.status === 'confirmed' || result.status === 'failed')) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          setLoading(false);
          console.log('[RequirementTab] polling done, calling onRefresh...');
          onRefresh();
          if (result.status === 'failed') {
            message.error('需求解析失败：' + (result?.error_message || '未知错误'));
          } else {
            message.success('需求解析完成');
          }
        }
      } catch (error) {
        console.error('[RequirementTab] polling error:', error);
      }
    }, POLL_CONFIG.INTERVAL);
  };

  // 确认需求
  const handleConfirm = async () => {
    if (!requirement?.id) {
      message.error('需求信息不存在');
      return;
    }
    setConfirming(true);
    try {
      await proposalApi.updateRequirement(proposalId, requirement.id, {
        confirmation_status: 'confirmed',
      });
      message.success('需求已确认');
      onRefresh();
    } catch (error) {
      message.error('确认失败，请重试');
    } finally {
      setConfirming(false);
    }
  };

  // 重新解析
  const handleReparse = () => {
    const rawInquiry = (requirement as unknown as Record<string, unknown>)?.inquiry_content;
    const rawContent = (requirement as unknown as Record<string, unknown>)?.raw_content;
    const content = String(rawInquiry || rawContent || '');
    if (content) {
      setInputContent(content);
      handleAnalyze(content);
    } else {
      handleAnalyze();
    }
  };

  // 解析结果数据
  const parsedResult = requirement?.parsed_result as Record<string, unknown> | undefined;
  console.log('[RequirementTab] render requirement status:', requirement?.status, 'company_name:', parsedResult?.company_name);

  // 是否允许编辑（draft/confirmed/failed 且有解析结果）
  const canEdit = !!parsedResult && ['draft', 'confirmed', 'failed'].includes(requirement?.status || '');

  // 字段分组 key 列表
  const companyKeys = ['company_name', 'brand', 'industry', 'website'];
  const productKeys = ['product_name', 'product_category', 'product_links'];
  const competitorKeys = ['competitors'];
  const marketingKeys = ['target_countries', 'target_platform', 'follower_tiers', 'content_types', 'budget_range', 'timeline', 'influencer_count'];

  // 数据来源映射（模拟）
  const fieldSources = parsedResult?.field_sources as Record<string, string> | undefined;

  // 知识库建议
  const kbSuggestions = parsedResult?.kb_suggestions as Array<{ field: string; suggestion: string }> | undefined;

  // 未生成状态（无 requirement 对象时显示输入表单）
  if (!requirement) {
    return (
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Text strong style={{ fontSize: 16 }}>客户询盘内容</Text>
        </div>
        <TextArea
          rows={8}
          placeholder="请粘贴客户询盘内容..."
          value={inputContent}
          onChange={(e) => setInputContent(e.target.value)}
          style={{ marginBottom: 16 }}
          disabled={loading}
        />
        <div style={{ marginBottom: 16 }}>
          <Text strong>询盘来源</Text>
          <div style={{ marginTop: 8 }}>
            <Radio.Group
              value={source}
              onChange={(e) => setSource(e.target.value)}
              disabled={loading}
            >
              {sourceOptions.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio>
              ))}
            </Radio.Group>
          </div>
        </div>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleAnalyze}
          loading={loading}
          disabled={!String(inputContent || '').trim()}
          size="large"
        >
          {loading ? 'AI 正在解析中...' : 'AI 智能解析'}
        </Button>
      </Card>
    );
  }

  // 处理中状态
  if (requirement.status === 'processing' || loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 24 }}>
            <Text type="secondary" style={{ fontSize: 16 }}>
              AI 正在智能解析客户需求，请稍候...
            </Text>
          </div>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">预计需要 30-60 秒</Text>
          </div>
        </div>
      </Card>
    );
  }

  // 失败状态 - 如果有已解析结果则继续显示（允许编辑），否则显示错误页
  if (requirement.status === 'failed' && !parsedResult) {
    return (
      <Card>
        <Result
          status="error"
          title="需求解析失败"
          subTitle={requirement.error_message || '解析过程中发生错误，请重试'}
          extra={
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleReparse}
            >
              重新解析
            </Button>
          }
        />
      </Card>
    );
  }

  // 已生成状态（含 failed 但有解析结果的情况）
  return (
    <Card>
      {/* 状态提示 */}
      <StatusAlert
        status={requirement?.status || 'draft'}
        confidenceScore={parsedResult?.confidence_score as number}
      />

      {/* 失败但有部分结果时的提示 */}
      {requirement.status === 'failed' && parsedResult && (
        <Alert
          type="warning"
          message="解析部分完成，您可以手动补充或修改以下字段"
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* 编辑/保存/取消按钮 */}
      {canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          {isEditing ? (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                保存
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
                disabled={saving}
              >
                取消
              </Button>
            </Space>
          ) : (
            <Button
              icon={<EditOutlined />}
              onClick={() => setIsEditing(true)}
            >
              编辑
            </Button>
          )}
        </div>
      )}

      {/* 公司信息 */}
      <Card title="公司信息" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small" bordered>
          {companyKeys.map((key) => (
            <Descriptions.Item
              key={key}
              label={
                <span>
                  {fieldLabelMap[key] || key}
                  {fieldSources?.[key] && <SourceTag source={fieldSources[key]} />}
                </span>
              }
            >
              {renderFieldValue(key)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      {/* 产品信息 */}
      <Card title="产品信息" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small" bordered>
          {productKeys.map((key) => (
            <Descriptions.Item
              key={key}
              label={
                <span>
                  {fieldLabelMap[key] || key}
                  {fieldSources?.[key] && <SourceTag source={fieldSources[key]} />}
                </span>
              }
            >
              {renderFieldValue(key)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      {/* 竞品信息 */}
      <Card title="竞品信息" size="small" style={{ marginBottom: 16 }}>
        <div>
          {renderFieldValue('competitors')}
          {!isEditing && fieldSources?.competitors && <SourceTag source={fieldSources.competitors} />}
        </div>
      </Card>

      {/* 营销目标 */}
      <Card title="营销目标" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small" bordered>
          {marketingKeys.map((key) => (
            <Descriptions.Item
              key={key}
              label={
                <span>
                  {fieldLabelMap[key] || key}
                  {fieldSources?.[key] && <SourceTag source={fieldSources[key]} />}
                </span>
              }
            >
              {renderFieldValue(key)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      {/* 知识库建议 */}
      {kbSuggestions && kbSuggestions.length > 0 && (
        <Card
          title={
            <Space>
              <InfoCircleOutlined style={{ color: COLORS.primary }} />
              <span>知识库建议</span>
            </Space>
          }
          size="small"
          style={{ marginBottom: 16, borderColor: COLORS.primary }}
        >
          <List
            size="small"
            dataSource={kbSuggestions}
            renderItem={(item) => (
              <List.Item>
                <Space>
                  <Tag color="blue">{fieldLabelMap[item.field] || item.field}</Tag>
                  <Text>{item.suggestion}</Text>
                </Space>
              </List.Item>
            )}
          />
        </Card>
      )}

      {/* 操作栏 */}
      <div style={{ marginTop: 24, borderTop: `1px solid ${COLORS.gray200}`, paddingTop: 16 }}>
        {requirement?.status !== 'confirmed' ? (
          <Space>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleConfirm}
              loading={confirming}
            >
              确认需求
            </Button>
            <Button icon={<SyncOutlined />} onClick={handleReparse}>
              重新解析
            </Button>
          </Space>
        ) : (
          <Alert
            type="success"
            message="需求已确认"
            icon={<CheckCircleOutlined />}
            showIcon
          />
        )}
      </div>

      {/* Tokens 消耗 */}
      {requirement?.tokens_used && (
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Text type="secondary">消耗 Tokens: {requirement.tokens_used}</Text>
        </div>
      )}
    </Card>
  );
};
